import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import {
  TextField,
} from "@mui/material";
import axios from "axios";
import { coopsData } from "../../utils/coopsData";
import img from "../../assets/images/comingsoon.jpg";

// Register necessary Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

type Props = {};

const Reports = (props: Props) => {
  const BASE_URL = process.env.REACT_APP_API_KEY;
  const [chartData, setChartData] = useState<any[]>([]);
  const [userRole, setUserRole] = useState(localStorage.getItem("role") || "");
  const [coopData, setCoopData] = useState({ systemList: [] });
  const today = new Date().toISOString().split("T")[0];
  const last7Days = new Date();
  last7Days.setDate(last7Days.getDate() - 7);
  const last7DaysFormatted = last7Days.toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(last7DaysFormatted);
  const [toDate, setToDate] = useState(today);
  const [isEditable, setIsEditable] = useState(true);
  const [selectedCoopKeys, setSelectedCoopKeys] = useState<string[]>(
    coopData?.systemList?.map((coop: any) => coop?.systemNumber)
  );

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [disable, setDisable] = useState(false);

  const [barCoopChartData, setCoopBarChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Submissions Count",
        data: [],
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  });

  const fetchCoops = async () => {
    const systemNumber = localStorage.getItem("systemNumber") || "";
    if (systemNumber === "15063") {
      const coopData = await coopsData();

      setCoopData(coopData);

      // Automatically select all coop keys when data is fetched

      const allCoopKeys = coopData.systemList.map(
        (coop: any) => coop.systemNumber
      );

      setSelectedCoopKeys(allCoopKeys);

      await fetchChart(coopData); // <-- Pass coopData as param
    } else {
      await fetchChart([]);
    }
  };

  type System = {
    systemNumber: string;
    systemName: string;
  };

  type DataItem = {
    _id: string;
    sysName?: string;
  };


  const fetchChart = async (coopsData: any) => {
    const SystemNumber = localStorage.getItem("systemNumber");
    const token = localStorage.getItem("authtoken") || "";

    if (userRole === "superadmin") {
    } else {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await axios.get(
          `${BASE_URL}/document/getCoopsListReports/${SystemNumber}?fromDate=${fromDate}&toDate=${toDate}`,
          config
        );

        const backendFiles = response.data.getchartData;
        if (backendFiles && backendFiles[0]) {
          const updatedPieChartData = {
            labels: backendFiles[0].labels || [],
            datasets: [
              {
                label: "Submissions Count",
                data: backendFiles[0].count || [],
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
              },
            ],
          };

          setCoopBarChartData(updatedPieChartData);
        } else {
          console.error("Invalid data structure returned from backend.");
        }

        const dataFiles = response.data.getsysNumchart;
        setChartData(dataFiles);
        setTotalPages(dataFiles.length);

      } catch (error) {
        console.error("Error fetching documents:", error);
      }
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      await fetchCoops();
      // await fetchChart(); // Will run after coops are set
    };
    fetchAll();
  }, []);


  useEffect(() => {
    const total = Math.ceil(chartData.length / itemsPerPage);
    setTotalPages(total);
  }, [chartData, itemsPerPage]);


  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(e.target.value));
    setPage(1); // reset to first page
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const renderPaginationRange = () => {
    const totalPages = Math.ceil(chartData.length / itemsPerPage);
    const pageNumbers = [];

    const maxPageButtons = 5;
    const halfRange = Math.floor(maxPageButtons / 2);

    let startPage = Math.max(1, page - halfRange);
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage < maxPageButtons - 1) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    // First page button (if not in range)
    if (startPage > 1) {
      pageNumbers.push(
        <button key={1} onClick={() => handlePageChange(1)} disabled={disable}>
          1
        </button>
      );
      if (startPage > 2) {
        pageNumbers.push(<span key="start-ellipsis">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          disabled={page === i || disable}
          className={page === i ? "active-page" : ""}
        >
          {i}
        </button>
      );
    }

    // Last page button (if not in range)
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key="end-ellipsis">...</span>);
      }
      pageNumbers.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          disabled={disable}
        >
          {totalPages}
        </button>
      );
    }

    return pageNumbers;
  };


  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleChartSearch = () => {
    fetchCoops();
  }

  const handleNextPage = () => {
    const totalPages = Math.ceil(chartData.length / itemsPerPage);
    if (page < Math.min(10, totalPages)) {
      setPage(page + 1);
    }
  };

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = chartData.slice(startIndex, endIndex);
  // const coopPaginatedData = coopchartData.slice(startIndex, endIndex);
  //   const totalPages = Math.ceil(chartData.length / itemsPerPage);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "30px",
      }}
    >
      {userRole === "superadmin" ? (
        <>
          <div>
            <img src={img} alt="Coming Soon" className="responsive-image" />
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              gap: "20px",
              marginBottom: "15px",
              flexWrap: "wrap",
            }}
          >
            <TextField
              id="fromDate"
              label="From Date"
              type="date"
              required
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                if (new Date(e.target.value) > new Date(toDate)) {
                  setToDate(""); // Reset toDate if fromDate is greater
                }
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
              InputProps={{
                readOnly: !isEditable,
                inputProps: {
                  max: toDate || today,
                },
              }}
            />

            <TextField
              id="toDate"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                if (new Date(e.target.value) < new Date(fromDate)) {
                  setFromDate(""); // Reset fromDate if toDate is less
                }
              }}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
              required
              InputProps={{
                readOnly: !isEditable,
                inputProps: {
                  min: fromDate,
                  max: today,
                },
              }}
            />

            <button
              className="blue"
              onClick={handleChartSearch}
              disabled={disable}
            >
              Search
            </button>

          </div>

          <div
            style={{
              padding: "20px",
              maxWidth: "600px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <h3>Form Submissions Chart</h3>
            <p style={{ fontWeight: "bold" }}>
              Total Submissions: {barCoopChartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0)}
            </p>
            <Bar data={barCoopChartData} />
          </div>

          {paginatedData.length > 0 && (
            <div style={{ overflowX: "auto", textAlign: "center" }}>
              <h3>System Submissions</h3>
              <p style={{ fontWeight: "bold" }}>
                Number of Users Submitted: {chartData.length}
              </p>
              <table
                cellPadding="10"
                cellSpacing="0"
                style={{ margin: "0 auto", borderCollapse: "collapse" }}
              >
                <thead>
                  <tr>
                    <th>User Name</th>
                    <th>Form Type</th>
                    <th>Submissions Count</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item: any) => (
                    <tr key={`${item.username}-${item.type}`}>
                      <td>{item.username}</td>
                      <td>{item.type}</td>
                      <td>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Items Per Page Dropdown */}
              <div style={{ marginTop: "15px" }}>
                <label>
                  Items per page: &nbsp;
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>

              {/* Pagination Buttons */}
              <div
                style={{
                  marginTop: "15px",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={handlePreviousPage}
                  disabled={page === 1 || disable}
                >
                  &lt;{" "}
                </button>
                {renderPaginationRange()}
                <button
                  onClick={handleNextPage}
                  disabled={page >= totalPages || disable}
                >
                  {" "}
                  &gt;
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
